import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { clampCoverFontWeight } from 'fym-shared/src/music24/coverDoc';
import { KIT_FONT_FACES, KIT_FONT_IDS, type KitFontId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../common/types';
import { kitFontName, kitFontStyle } from '../../music24/promote/themes/kitWebFonts';
import { coverEditorMessages } from '../coverEditorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FontPickerProps {
  value: KitFontId;
  /** The display font of the release theme of the song: it gets the "Theme" mark. */
  themeFontId: KitFontId | null;
  disabled?: boolean;
  onChange: (change: { fontId: KitFontId; weight: number; italic: boolean }) => void;
}

/**
 * The font choice of the text inspector (design cover-editor d1): the 9 kit fonts, each one shown
 * in its own face. A pick sets the display weight and the slant of the font.
 */
const FontPickerComponent: React.FC<FontPickerProps> = ({ value, themeFontId, disabled, onChange }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((open) => !open), []);
  const pick = useCallback(
    (fontId: KitFontId) => {
      const face = KIT_FONT_FACES[fontId];
      onChange({ fontId, weight: clampCoverFontWeight(fontId, face.displayWeight), italic: face.italic });
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <span className={styles.fontPicker}>
      <button
        type="button"
        className={styles.field}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={toggle}
      >
        <span style={kitFontStyle(value)}>{kitFontName(value)}</span>
        <span className={styles.fieldCaret} aria-hidden>
          ▾
        </span>
      </button>
      {isOpen && (
        <span className={styles.fontList} role="listbox" aria-label={intl.formatMessage(coverEditorMessages.textFont)}>
          {KIT_FONT_IDS.map((fontId) => (
            <button
              key={fontId}
              type="button"
              role="option"
              aria-selected={fontId === value}
              className={clsx(styles.fontOption, fontId === value && styles.fontOptionOn)}
              onClick={() => pick(fontId)}
            >
              <span style={kitFontStyle(fontId)}>{kitFontName(fontId)}</span>
              {fontId === themeFontId && (
                <span className={styles.fontTheme}>{intl.formatMessage(coverEditorMessages.textFontTheme)}</span>
              )}
            </button>
          ))}
        </span>
      )}
    </span>
  );
};

export const FontPicker = TypedMemo(FontPickerComponent);
